import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@chakra-ui/react';
import { LuChevronsUpDown } from 'react-icons/lu';

import { CreditsBalanceRow, UserMenu } from './components';
import { POPOVER_Z_INDEX, useHiUserPopover } from './hooks/useHiUserPopover';
import { useIntegrationsStore } from '../../../../store';
import { SidebarRailTooltip } from '../../../SidebarRailTooltip/SidebarRailTooltip';

export interface HiUserProps {
  userName: string;
  userEmail: string;
  isOpen: boolean;
  onNavigate?: () => void;
}

const stylesheet = {
  relativeWrapper: 'relative w-full',
  railTooltipAnchor: 'w-full',
  cardOpen: 'rounded-semantic border border-semantic-border overflow-hidden',
  profileRowOpen: `flex items-center gap-2 group bg-semantic-bg-surface p-3 cursor-pointer hover:bg-semantic-bg-hover transition-colors duration-150 ${POPOVER_Z_INDEX.POPOVER}`,
  containerClosed:
    'flex items-center gap-2 group bg-semantic-bg-surface p-3 rounded-semantic border border-transparent',
  infosWrapper: 'flex-1 min-w-0',
  avatar: 'h-[42px] w-[42px]',
  button: 'flex-shrink-0',
  buttonDisabled: 'flex-shrink-0 opacity-60 cursor-not-allowed',
  userName: 'text-sm font-bold text-semantic-text-primary',
  userEmail: 'text-sm text-semantic-text-secondary truncate',
  icon: 'text-semantic-text-secondary',
  backdrop: `fixed inset-0 ${POPOVER_Z_INDEX.BACKDROP}`,
};

export function HiUser({
  userName,
  userEmail,
  isOpen,
  onNavigate,
}: Readonly<HiUserProps>) {
  const { t } = useTranslation();
  const {
    currentIntegrationAccount,
    integrationAccounts,
    setCurrentIntegrationAccount,
    isDraftSaving,
  } = useIntegrationsStore();

  const {
    popoverAnchor,
    popoverRect,
    isOptionOpen,
    onCloseOption,
    handleToggle,
  } = useHiUserPopover({ isSidebarOpen: isOpen });

  const portalNode = isOptionOpen ? (
    <>
      <div
        className={stylesheet.backdrop}
        onClick={onCloseOption}
        role="presentation"
      />
      <UserMenu
        userName={userName}
        isOptionOpen={isOptionOpen}
        integrationAccounts={integrationAccounts}
        setCurrentIntegrationAccount={setCurrentIntegrationAccount}
        onOptionClose={onCloseOption}
        currentIntegrationAccount={currentIntegrationAccount}
        triggerRect={popoverRect}
        onNavigate={onNavigate}
      />
    </>
  ) : null;

  const handleClick = isDraftSaving ? undefined : handleToggle;
  const handleKeyDown = isDraftSaving
    ? undefined
    : (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      };

  const profileRow = (
    <div
      ref={popoverAnchor}
      className={
        isOpen ? stylesheet.profileRowOpen : stylesheet.containerClosed
      }
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isDraftSaving ? -1 : 0}
      aria-disabled={isDraftSaving || undefined}
      aria-label={isOpen ? undefined : userName || undefined}
    >
      <Avatar
        className={stylesheet.avatar}
        variant="emerald"
        name={userName}
        color="white"
      />
      {isOpen && (
        <>
          <div className={stylesheet.infosWrapper}>
            <p className={stylesheet.userName}>{userName}</p>
            <p className={stylesheet.userEmail}>
              {currentIntegrationAccount?.displayName ?? userEmail}
            </p>
          </div>
          <span
            className={
              isDraftSaving ? stylesheet.buttonDisabled : stylesheet.button
            }
            title={
              isDraftSaving
                ? t('inbox.messageDialog.cannotChangeEmail')
                : undefined
            }
          >
            <LuChevronsUpDown className={stylesheet.icon} />
          </span>
        </>
      )}
    </div>
  );

  const collapsedRow = userName ? (
    <SidebarRailTooltip label={userName} isDisabled={isOptionOpen}>
      <div className={stylesheet.railTooltipAnchor}>{profileRow}</div>
    </SidebarRailTooltip>
  ) : (
    profileRow
  );

  return (
    <div className={stylesheet.relativeWrapper}>
      {portalNode && createPortal(portalNode, document.body)}
      {isOpen ? (
        <div className={stylesheet.cardOpen}>
          {profileRow}
          <CreditsBalanceRow />
        </div>
      ) : (
        collapsedRow
      )}
    </div>
  );
}
